import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthGuard } from '../App'; // Assuming AuthGuard is correctly set up
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSms, faUser, faBuilding, faPaperPlane, faSpinner, faCommentDots, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons'; // Added faExclamationTriangle
import { StandardURL } from "../App";

const SendSMSPage = () => {
  const [activeTab, setActiveTab] = useState('single'); // 'single' or 'all'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [propertyId, setPropertyId] = useState(null); // State to store propertyId
  const CHARACTER_LIMIT = 150;

  // List of common offensive words (you can expand this significantly)
  const insultKeywords = [
    'idiot', 'fool', 'stupid', 'dumb', 'moron', 'asshole', 'bitch', 'bastard',
    'cunt', 'shit', 'damn', 'hell', 'fuck', 'wanker', 'loser', 'ugly', 'useless'
    // Add more as needed
  ];

  // Effect to load propertyId from localStorage on component mount
  useEffect(() => {
    const storedPropertyId = localStorage.getItem("selectedPropertyId");
    if (storedPropertyId) {
      setPropertyId(storedPropertyId);
    } else {
      // Handle case where propertyId is not found (e.g., redirect or show error)
      toast.error("No property ID found. Please select a property first. ⚠️");
      // You might want to redirect the user here, e.g.: navigate('/select-property');
    }
  }, []); // Empty dependency array means this runs once on mount

  const containsInsult = (text) => {
    const lowerCaseText = text.toLowerCase();
    return insultKeywords.some(keyword => lowerCaseText.includes(keyword));
  };

  const handleMessageChange = (e) => {
    const text = e.target.value;
    if (text.length <= CHARACTER_LIMIT) {
      setMessage(text);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!propertyId) {
      toast.error("Property ID is missing. Cannot send SMS. 🚫");
      setIsLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      toast.error("Authentication token not found. Please log in again. 🔑");
      setIsLoading(false);
      return;
    }

    if (message.trim() === '') {
      toast.error("Message cannot be empty. 📝");
      setIsLoading(false);
      return;
    }

    if (containsInsult(message)) {
      toast.error(
        <div className="flex items-center">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 text-xl" />
          <span>Message contains inappropriate language. Please revise. 🤬</span>
        </div>,
        { autoClose: 7000 } // Keep insult warning on screen longer
      );
      setIsLoading(false);
      return;
    }

    let endpoint = '';
    let payload = {};

    if (activeTab === 'single') {
      if (phoneNumber.trim() === '') {
        toast.error("Phone number cannot be empty when sending to a single person. 📞");
        setIsLoading(false);
        return;
      }
      // Basic phone number validation (you might want a more robust regex)
      if (!/^\+?[0-9\s-]{7,20}$/.test(phoneNumber)) {
        toast.error("Please enter a valid phone number. 🔢");
        setIsLoading(false);
        return;
      }
      endpoint = `${StandardURL}/iNyumba/admin/sms/send-one/${propertyId}`;
      payload = { phone: phoneNumber, message };
    } else { // activeTab === 'all'
      endpoint = `${StandardURL}/iNyumba/admin/sms/send-property/${propertyId}`;
      payload = { message };
    }

    try {
      const response = await axios.post(
        endpoint,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data && response.data.success) {
        toast.success(response.data.response || "SMS sent successfully! 🚀");
        // Clear form fields on success
        setPhoneNumber('');
        setMessage('');
      } else {
        toast.error(response.data.response || "Failed to send SMS. Please try again. 😔");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.response || "An unexpected error occurred. Please try again later. 🚧";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4 sm:p-6">
      
        <div className="bg-white p-8 sm:p-10 rounded-3xl shadow-3xl w-full max-w-md animate-fade-in-up border border-gray-100">
          <div className="text-center mb-8">
            <FontAwesomeIcon icon={faSms} className="text-purple-600 text-6xl mb-4 animate-bounce-in" />
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 leading-tight">Send SMS Message</h2>
            <p className="text-gray-600 mt-3 text-lg">Communicate instantly with your tenants.</p>
          </div>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8 space-x-4">
            <button
              className={`py-2 px-6 rounded-full text-md font-semibold transition-all duration-300 ${
                activeTab === 'single'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => {
                setActiveTab('single');
                setPhoneNumber(''); // Clear phone number when switching tabs
              }}
            >
              <FontAwesomeIcon icon={faUser} className="mr-2" /> Send to One
            </button>
            <button
              className={`py-2 px-6 rounded-full text-md font-semibold transition-all duration-300 ${
                activeTab === 'all'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveTab('all')}
            >
              <FontAwesomeIcon icon={faBuilding} className="mr-2" /> Send to All Tenants
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {activeTab === 'single' && (
              <div>
                <label htmlFor="phone-number" className="block text-gray-700 text-sm font-semibold mb-2">
                  <FontAwesomeIcon icon={faCommentDots} className="mr-2 text-gray-500" />
                  Tenant Phone Number:
                </label>
                <input
                  type="tel" // Use type="tel" for phone numbers
                  id="phone-number"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none shadow-sm"
                  placeholder="e.g., +254712345678 or 0712345678"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  disabled={isLoading}
                  aria-label="Tenant Phone Number"
                />
              </div>
            )}

            <div>
              <label htmlFor="message" className="block text-gray-700 text-sm font-semibold mb-2">
                <FontAwesomeIcon icon={faCommentDots} className="mr-2 text-gray-500" />
                Message:
              </label>
              <textarea
                id="message"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-200 outline-none shadow-sm resize-y min-h-[100px]"
                placeholder={`Type your message here (Max ${CHARACTER_LIMIT} characters)...`}
                value={message}
                onChange={handleMessageChange}
                rows="4"
                maxLength={CHARACTER_LIMIT}
                required
                disabled={isLoading}
                aria-label="SMS Message"
              ></textarea>
              <p className="text-right text-sm text-gray-500 mt-1">
                {message.length} / {CHARACTER_LIMIT} characters
              </p>
            </div>

            <button
              type="submit"
              className={`w-full py-3.5 px-6 rounded-lg text-lg font-bold flex items-center justify-center transition-all duration-300 ease-in-out transform hover:scale-105
                ${isLoading
                  ? 'bg-blue-400 cursor-not-allowed opacity-80'
                  : 'bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 shadow-lg'
                }`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-3 text-xl" />
                  Sending SMS...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPaperPlane} className="mr-3 text-xl" />
                  Send SMS
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </AuthGuard>
  );
};

export default SendSMSPage;